import { APIService } from '../../../lib/api/APIService';
import { API_CONFIG } from '../../../config/api';

/**
 * Per HTTP request timeout (start job, poll status, assemble).
 * Does not cap total full-exam wall time — polling runs until the server job finishes.
 */
const LNAT_JOB_TIMEOUT_MS = 300000;

class ExamStudioAPI extends APIService {
  constructor() {
    super({
      timeout: LNAT_JOB_TIMEOUT_MS,
      retryAttempts: 5,
      retryDelay: 1500,
    });
  }

  async listLnatPassages() {
    return this.get('/api/studio-exams/lnat/passages', { timeout: LNAT_JOB_TIMEOUT_MS });
  }

  async buildLnatPassageRag(formData) {
    await this.ensureAuthFresh();
    const url = `${API_CONFIG.baseURL}/api/studio-exams/lnat/rag/build`;
    const token = this.getAccessToken();
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
      signal: AbortSignal.timeout(LNAT_JOB_TIMEOUT_MS),
    });
    if (res.status === 401) {
      await this.refreshAccessToken();
      const token2 = this.getAccessToken();
      const retry = await fetch(url, {
        method: 'POST',
        headers: {
          ...(token2 ? { Authorization: `Bearer ${token2}` } : {}),
        },
        body: formData,
        signal: AbortSignal.timeout(LNAT_JOB_TIMEOUT_MS),
      });
      if (!retry.ok) {
        const err = await retry.json().catch(() => ({}));
        throw new Error(err.error || `RAG build failed (${retry.status})`);
      }
      return retry.json();
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `RAG build failed (${res.status})`);
    }
    return res.json();
  }

  async startLnatJob(step, payload = {}) {
    return this.post(
      '/api/studio-exams/lnat/jobs/start',
      { step, payload },
      { timeout: LNAT_JOB_TIMEOUT_MS },
    );
  }

  async lnatJobStatus(jobId) {
    return this.get(
      `/api/studio-exams/lnat/jobs/${encodeURIComponent(jobId)}`,
      { timeout: LNAT_JOB_TIMEOUT_MS },
    );
  }

  async getLnatSessionProgress(sessionId) {
    await this.ensureAuthFresh();
    return this.get(
      `/api/studio-exams/lnat/sessions/${encodeURIComponent(sessionId)}/progress`,
      { timeout: LNAT_JOB_TIMEOUT_MS },
    );
  }

  async assembleLnatFullExam(sessionId, payload = {}) {
    return this.post(
      `/api/studio-exams/lnat/sessions/${encodeURIComponent(sessionId)}/assemble-exam`,
      payload,
      { timeout: LNAT_JOB_TIMEOUT_MS },
    );
  }

  async exportLnatSessionToExamBuilderJson(sessionId, payload = {}) {
    await this.ensureAuthFresh();
    return this.post(
      `/api/studio-exams/lnat/sessions/${encodeURIComponent(sessionId)}/export`,
      payload,
      { timeout: LNAT_JOB_TIMEOUT_MS },
    );
  }

  /** Sync browser passage bundles and push partial full-exam progress to ExamBuilderJson. */
  async saveLnatPartialProgressToRepo(sessionId, payload = {}) {
    await this.ensureAuthFresh();
    return this.post(
      `/api/studio-exams/lnat/sessions/${encodeURIComponent(sessionId)}/export`,
      { partial: true, ...payload },
      { timeout: LNAT_JOB_TIMEOUT_MS },
    );
  }
}

export const examStudioAPI = new ExamStudioAPI();
